import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  UploadCloud, ArrowRight, ArrowUpRight, ShieldCheck, Lock,
  ReceiptText, HelpCircle, Scale, AlertTriangle, CheckCircle2,
  FileText, Search, GitCompare, MessageSquare,
  PenLine, ShieldAlert, MessagesSquare, TrendingDown, Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { track, captureUtm } from "@/lib/okkio";
import { FAQS } from "@/lib/content";

const ease = [0.22, 1, 0.36, 1];
const up = { hidden: { opacity: 0, y: 16 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.5, ease } }) };

const PRICE_LINE = "Anteprima gratuita • Report completo €9,99 • pagamento una tantum";

const findings = [
  { icon: ReceiptText, t: "Costi mancanti", d: "Lavorazioni che potrebbero essere necessarie ma non risultano indicate." },
  { icon: HelpCircle, t: "Voci poco chiare", d: "Descrizioni troppo generiche per capire esattamente cosa stai acquistando." },
  { icon: Scale, t: "Prezzi da verificare", d: "Voci che meritano un confronto o una spiegazione più dettagliata." },
  { icon: AlertTriangle, t: "Possibili extracosti", d: "Punti che potrebbero generare costi o discussioni dopo l’inizio dei lavori." },
];

const benefits = [
  { icon: PenLine, t: "Firma con più consapevolezza", d: "Sai quali punti chiarire prima di accettare." },
  { icon: ShieldAlert, t: "Evita sorprese evitabili", d: "Individui prima le voci che potrebbero trasformarsi in extracosti." },
  { icon: MessagesSquare, t: "Parla meglio con l’impresa", d: "Ricevi domande precise invece di un generico “è tutto compreso?”." },
  { icon: TrendingDown, t: "Tratta dove ha senso", d: "Capisci quali prezzi meritano un approfondimento." },
];

const steps = [
  { icon: FileText, t: "Leggiamo il preventivo", d: "Analizziamo voci, importi, quantità e descrizioni." },
  { icon: Search, t: "Cerchiamo ciò che merita attenzione", d: "Omissioni, ambiguità, possibili extracosti e prezzi da verificare." },
  { icon: GitCompare, t: "Confrontiamo dove abbiamo riferimenti affidabili", d: "Senza promettere confronti locali quando i dati non bastano." },
  { icon: MessageSquare, t: "Ti diciamo cosa chiedere", d: "Report leggibile anche se non sei un tecnico." },
];

const homeFaqs = [
  { q: "Quanto costa controllare un preventivo?", a: "L’anteprima è gratuita. Il report completo costa €9,99 una sola volta. Nessun abbonamento." },
  { q: "Cosa controlla OKKIO?", a: "Controlla voci poco chiare, possibili costi mancanti, prezzi da verificare e punti che potrebbero generare extracosti o discussioni dopo l’inizio dei lavori." },
  { q: "OKKIO mi dice se devo accettare o rifiutare il preventivo?", a: "No. Ti aiuta a capire cosa chiarire prima di firmare. La decisione finale resta sempre tua." },
  { q: "OKKIO è un’impresa edile o vende lavori?", a: "No. OKKIO è indipendente: non esegue lavori e non riceve commissioni dalle imprese che analizzi." },
  { q: "Posso caricare un preventivo in PDF o foto?", a: "Sì. Puoi caricare PDF, JPG o PNG, purché il testo sia leggibile." },
  { q: "I miei documenti sono al sicuro?", a: "I documenti vengono usati per effettuare l’analisi richiesta. Per i dettagli su conservazione e trattamento consulta l’informativa Privacy." },
  { q: "Posso usare il report per parlare con l’impresa?", a: "Sì. Il report evidenzia cosa chiedere e ti aiuta a preparare domande più precise prima di firmare." },
  { q: "OKKIO garantisce che non ci saranno extracosti?", a: "No. Nessun controllo può garantire cosa accadrà durante i lavori. OKKIO serve a ridurre le zone poco chiare prima della firma." },
];

function StickyCta({ onStart }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const nearBottom = window.innerHeight + y >= document.body.offsetHeight - 260;
      setShow(y > 460 && !nearBottom);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      data-testid="sticky-mobile-cta"
      className={`lg:hidden fixed inset-x-0 bottom-0 z-40 px-4 pb-[max(12px,env(safe-area-inset-bottom))] pt-3 bg-white/95 backdrop-blur-md border-t border-[#E2E8F0] transition-all duration-300 ${show ? "translate-y-0 opacity-100" : "translate-y-full opacity-0 pointer-events-none"}`}>
      <Button data-testid="sticky-cta-btn" onClick={onStart}
        className="w-full rounded-xl h-14 text-base font-semibold bg-[#3157F6] hover:bg-[#2444D8] text-white shadow-[0_10px_28px_-10px_rgba(49,87,246,0.7)] active:scale-[0.98] transition-transform">
        Controlla il mio preventivo <ArrowRight className="w-5 h-5 ml-1" strokeWidth={2.2} />
      </Button>
      <p className="mt-1.5 text-center text-xs font-medium text-[#64748B]">Anteprima gratuita</p>
    </div>
  );
}

const SectionTitle = ({ children }) => (
  <h2 className="font-heading text-2xl font-semibold tracking-tight text-[#0F172A] leading-snug">{children}</h2>
);

export default function Landing() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { captureUtm(); track("landing_view"); }, []);
  useEffect(() => {
    if (location.hash !== "#faq") return;
    let count = 0;
    const iv = setInterval(() => {
      document.getElementById("faq")?.scrollIntoView({ behavior: "smooth", block: "start" });
      if (++count >= 6) clearInterval(iv);
    }, 200);
    return () => clearInterval(iv);
  }, [location.hash, location.key]);
  const start = () => { track("upload_start"); navigate("/controlla-preventivo"); };

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <Seo title="" path="/" faq={FAQS.slice(0, 6)}
        description="OKKIO controlla il preventivo della tua ristrutturazione: costi mancanti, voci poco chiare e prezzi da verificare. Anteprima gratuita, report completo €9,99." />
      <Navbar />

      {/* B. HERO */}
      <section className="max-w-xl mx-auto px-5 pt-5 pb-8">
        <motion.h1 variants={up} initial="hidden" animate="show" custom={0}
          className="font-heading text-[1.58rem] sm:text-4xl font-semibold tracking-tight text-[#0F172A] leading-[1.15]">
          Hai ricevuto un preventivo per i lavori di casa? Controllalo prima di firmare.
        </motion.h1>
        <motion.p variants={up} initial="hidden" animate="show" custom={1}
          className="mt-3 text-base sm:text-lg text-[#334155] leading-relaxed">
          Carica PDF o foto del preventivo. Prima analisi gratuita, nessuna registrazione, risultato in pochi minuti.
        </motion.p>

        <motion.div variants={up} initial="hidden" animate="show" custom={2} className="mt-5">
          <Button data-testid="hero-cta" onClick={start}
            className="w-full rounded-2xl h-16 px-6 text-lg font-semibold bg-[#3157F6] hover:bg-[#2444D8] text-white shadow-[0_14px_34px_-12px_rgba(49,87,246,0.75)] active:scale-[0.98] transition-transform">
            <UploadCloud className="w-6 h-6 mr-2" strokeWidth={2} /> Carica il mio preventivo
          </Button>
          <p className="mt-2.5 text-center text-sm font-medium text-[#64748B]" data-testid="hero-microcopy">
            PDF o foto • Nessuna registrazione • Prima analisi gratuita
          </p>
        </motion.div>

        <motion.div variants={up} initial="hidden" animate="show" custom={3}
          className="mt-5 grid grid-cols-3 gap-2 text-center" data-testid="hero-trust">
          {[
            { icon: ShieldCheck, t: "Analisi indipendente", c: "#3157F6" },
            { icon: Lock, t: "Dati riservati", c: "#64748B" },
            { icon: CheckCircle2, t: "Nessun abbonamento", c: "#16A36A" },
          ].map((x) => (
            <div key={x.t} className="rounded-xl border border-[#E2E8F0] bg-white px-2 py-3 flex flex-col items-center gap-1.5">
              <x.icon className="w-5 h-5" style={{ color: x.c }} strokeWidth={2} />
              <span className="text-xs font-semibold text-[#334155] leading-tight">{x.t}</span>
            </div>
          ))}
        </motion.div>
        <p className="mt-3 text-center text-xs text-[#94A3B8]" data-testid="hero-price-line">{PRICE_LINE}</p>
      </section>

      {/* C. MINI-REPORT */}
      <section className="max-w-xl mx-auto px-5 py-6">
        <SectionTitle>Ecco cosa ricevi</SectionTitle>
        <div className="mt-4 rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid="mini-report">
          <div className="flex items-end gap-3">
            <span className="font-heading text-5xl font-semibold text-[#0F172A] leading-none">61</span>
            <span className="text-[#64748B] mb-1.5">/ 100</span>
            <span className="ml-auto inline-flex items-center rounded-full bg-[#F59E0B]/10 text-[#B45309] px-2.5 py-1 text-xs font-semibold">Esempio dimostrativo</span>
          </div>
          <p className="mt-2 text-[#334155] font-medium">Preventivo da chiarire prima di firmare</p>
          <div className="mt-5 space-y-2.5">
            {[["5", "cose da controllare"], ["3", "possibili costi mancanti"], ["2", "prezzi da verificare"]].map(([n, l]) => (
              <div key={l} className="flex items-center gap-3">
                <span className="font-heading text-xl font-semibold text-[#0F172A] w-8">{n}</span>
                <span className="text-[#334155]">{l}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 flex items-start gap-3 rounded-xl bg-[#DC3F4C]/[0.06] border border-[#DC3F4C]/20 p-4">
            <AlertTriangle className="w-5 h-5 text-[#DC3F4C] flex-shrink-0 mt-0.5" strokeWidth={2} />
            <div>
              <p className="font-heading font-semibold text-[#0F172A] leading-snug">Smaltimento macerie non specificato</p>
              <p className="mt-1 text-sm text-[#334155] leading-relaxed">Potrebbe diventare un costo extra. Chiedi se è incluso nel prezzo.</p>
            </div>
          </div>
          <Link to="/esempio-report" data-testid="mini-report-cta"
            className="mt-5 inline-flex items-center justify-center gap-1.5 w-full rounded-xl h-12 border border-[#C7D2FE] bg-[#EEF2FF] text-[#2444D8] font-semibold hover:bg-[#E0E7FF] transition-colors">
            Guarda un report completo <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
          </Link>
        </div>
      </section>

      {/* D. COSA PUÒ TROVARE OKKIO */}
      <section className="max-w-xl mx-auto px-5 py-6">
        <SectionTitle>Cosa può trovare OKKIO nel tuo preventivo</SectionTitle>
        <div className="mt-5 space-y-3">
          {findings.map((c, i) => (
            <motion.div key={c.t} variants={up} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="flex items-start gap-4 rounded-2xl border border-[#E2E8F0] bg-white px-5 py-4" data-testid={`finding-${i}`}>
              <span className="w-10 h-10 rounded-xl bg-[#3157F6]/10 flex items-center justify-center flex-shrink-0">
                <c.icon className="w-5 h-5 text-[#3157F6]" strokeWidth={1.9} />
              </span>
              <div>
                <p className="font-heading text-base font-semibold text-[#0F172A] leading-tight">{c.t}</p>
                <p className="mt-0.5 text-sm text-[#64748B] leading-snug">{c.d}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* E. BENEFICI */}
      <section className="max-w-xl mx-auto px-5 py-6">
        <SectionTitle>Non serve sapere tutto di edilizia. Serve sapere cosa chiedere.</SectionTitle>
        <div className="mt-5 grid grid-cols-1 gap-3">
          {benefits.map((b, i) => (
            <motion.div key={b.t} variants={up} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="flex items-start gap-4 rounded-2xl bg-white border border-[#E2E8F0] px-5 py-4" data-testid={`benefit-${i}`}>
              <span className="w-10 h-10 rounded-xl bg-[#16A36A]/10 flex items-center justify-center flex-shrink-0">
                <b.icon className="w-5 h-5 text-[#16A36A]" strokeWidth={1.9} />
              </span>
              <div>
                <p className="font-heading text-base font-semibold text-[#0F172A] leading-tight">{b.t}</p>
                <p className="mt-0.5 text-sm text-[#64748B] leading-snug">{b.d}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* F. INDIPENDENZA */}
      <section className="max-w-xl mx-auto px-5 py-6">
        <div className="rounded-2xl border border-[#E2E8F0] bg-[#0F172A] text-white p-6" data-testid="trust-block">
          <span className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center mb-4">
            <ShieldCheck className="w-6 h-6 text-[#93B4FF]" strokeWidth={1.9} />
          </span>
          <h2 className="font-heading text-2xl font-semibold tracking-tight leading-snug">Il nostro cliente sei tu, non l’impresa.</h2>
          <p className="mt-3 text-[#CBD5E1] leading-relaxed">
            OKKIO non esegue lavori, non vende ristrutturazioni e non guadagna se scegli un’impresa invece di un’altra. Ti dà una seconda opinione indipendente prima di firmare.
          </p>
        </div>
      </section>

      {/* G. COME NASCE IL CONTROLLO */}
      <section className="max-w-xl mx-auto px-5 py-6">
        <SectionTitle>Come nasce il tuo controllo</SectionTitle>
        <div className="mt-5 space-y-3">
          {steps.map((s, i) => (
            <motion.div key={s.t} variants={up} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="flex items-start gap-4 rounded-2xl border border-[#E2E8F0] bg-white px-5 py-4" data-testid={`step-${i}`}>
              <span className="w-9 h-9 rounded-full bg-[#3157F6] text-white flex items-center justify-center flex-shrink-0 font-heading font-semibold">
                {i + 1}
              </span>
              <div>
                <p className="font-heading text-base font-semibold text-[#0F172A] leading-tight">{s.t}</p>
                <p className="mt-0.5 text-sm text-[#64748B] leading-snug">{s.d}</p>
              </div>
            </motion.div>
          ))}
        </div>
        <Link to="/metodologia" data-testid="home-metodologia-link"
          className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#3157F6] hover:underline">
          Scopri la metodologia <ArrowUpRight className="w-4 h-4" strokeWidth={2.2} />
        </Link>
      </section>

      {/* H. UNA SECONDA OPINIONE, NON UNA PERIZIA */}
      <section className="max-w-xl mx-auto px-5 py-6">
        <div className="flex items-start gap-3 rounded-2xl border border-[#E2E8F0] bg-[#EEF2FF] p-5" data-testid="limits-block">
          <Info className="w-5 h-5 text-[#3157F6] flex-shrink-0 mt-0.5" strokeWidth={2} />
          <div>
            <p className="font-heading text-base font-semibold text-[#0F172A]">Una seconda opinione, non una perizia.</p>
            <p className="mt-1 text-sm text-[#475569] leading-relaxed">
              OKKIO ti aiuta a individuare punti da chiarire nel preventivo prima di firmare. Non sostituisce un sopralluogo o una verifica tecnica professionale quando necessari.
            </p>
          </div>
        </div>
      </section>

      {/* I. FAQ */}
      <section id="faq" className="max-w-xl mx-auto px-5 py-6 scroll-mt-20">
        <SectionTitle>Domande frequenti</SectionTitle>
        <div className="mt-4 rounded-2xl border border-[#E2E8F0] bg-white px-5 py-1" data-testid="home-faq">
          <Accordion type="single" collapsible>
            {homeFaqs.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-[#E2E8F0]" data-testid={`home-faq-item-${i}`}>
                <AccordionTrigger className="text-base font-heading font-semibold text-[#0F172A] hover:no-underline py-4">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[15px] text-[#334155] leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* J. CTA FINALE */}
      <section className="max-w-xl mx-auto px-5 pt-4 pb-24 lg:pb-16">
        <div className="rounded-3xl bg-[#3157F6] text-white p-7 text-center">
          <h2 className="font-heading text-2xl font-semibold tracking-tight">Prima di firmare, controlla tutto.</h2>
          <Button data-testid="final-cta" onClick={start}
            className="mt-5 w-full rounded-xl h-14 px-6 text-base font-semibold bg-white text-[#3157F6] hover:bg-[#EAEFFE] active:scale-[0.98] transition-transform">
            Controlla il mio preventivo <ArrowRight className="w-5 h-5 ml-1" />
          </Button>
          <p className="mt-3 text-sm font-medium text-white/85" data-testid="final-price-line">{PRICE_LINE}</p>
        </div>
      </section>

      <Footer />
      <StickyCta onStart={start} />
    </div>
  );
}
